import { supabase } from '../config/supabase';
import {
  ProfessionalCompensationModel,
  ProfessionalFinancialRole,
  ProfessionalFinancialRoleStatus,
  ProfessionalFinancialRoleType,
} from '../types/professional-financial-role';

export interface SaveProfessionalFinancialRoleInput {
  clientId: string;
  financialRoleId?: string | null;
  professionalId: string;
  financialRoleType: ProfessionalFinancialRoleType;
  compensationModel: ProfessionalCompensationModel;
  fixedAmount?: number | null;
  effectiveFrom: string;
  effectiveUntil?: string | null;
  roleStatus: ProfessionalFinancialRoleStatus;
  notes?: string | null;
}

export class ProfessionalFinancialRoleService {
  static async generateMonthlyExpenses(input: {
    clientId: string;
    competence: string;
    dueDate?: string | null;
    variableAmounts: Record<string, number>;
  }): Promise<{ createdCount: number; existingCount: number; missingCount: number; missing: Array<{ financialRoleId: string; professionalName: string; reason: string }> }> {
    const { data, error } = await supabase.rpc('financial_a14_generate_role_expenses', {
      p_client_id: input.clientId,
      p_competence: `${input.competence}-01`,
      p_variable_amounts: input.variableAmounts,
      p_due_date: input.dueDate || null,
    });
    if (error) throw normalizeRoleError(error);
    if (!data || typeof data.createdCount !== 'number') throw new Error('A geração de obrigações retornou um formato inválido.');
    return data as { createdCount: number; existingCount: number; missingCount: number; missing: Array<{ financialRoleId: string; professionalName: string; reason: string }> };
  }

  static async listByClient(clientId: string): Promise<ProfessionalFinancialRole[]> {
    const { data, error } = await supabase
      .from('professional_financial_roles')
      .select('*')
      .eq('clientId', clientId)
      .is('archivedAt', null)
      .order('roleStatus', { ascending: true })
      .order('effectiveFrom', { ascending: false });

    if (error) throw normalizeRoleError(error);
    return (data || []) as ProfessionalFinancialRole[];
  }

  static async save(input: SaveProfessionalFinancialRoleInput): Promise<ProfessionalFinancialRole> {
    const payload = {
      clientId: input.clientId,
      professionalId: input.professionalId,
      financialRoleType: input.financialRoleType,
      compensationModel: input.compensationModel,
      fixedAmount: input.fixedAmount ?? null,
      effectiveFrom: input.effectiveFrom,
      effectiveUntil: input.effectiveUntil || null,
      roleStatus: input.roleStatus,
      notes: input.notes?.trim() || null,
      updatedAt: new Date().toISOString(),
    };

    const query = input.financialRoleId
      ? supabase
          .from('professional_financial_roles')
          .update(payload)
          .eq('financialRoleId', input.financialRoleId)
          .eq('clientId', input.clientId)
          .is('archivedAt', null)
      : supabase
          .from('professional_financial_roles')
          .insert({ ...payload, createdAt: new Date().toISOString() });

    const { data, error } = await query.select('*').single();
    if (error) throw normalizeRoleError(error);
    return data as ProfessionalFinancialRole;
  }

  static async setStatus(clientId: string, financialRoleId: string, roleStatus: ProfessionalFinancialRoleStatus): Promise<void> {
    const { error } = await supabase
      .from('professional_financial_roles')
      .update({ roleStatus, updatedAt: new Date().toISOString() })
      .eq('clientId', clientId)
      .eq('financialRoleId', financialRoleId)
      .is('archivedAt', null);

    if (error) throw normalizeRoleError(error);
  }
}

function normalizeRoleError(error: { code?: string; message?: string }): Error {
  if (error.code === '42P01' || error.code === 'PGRST205') {
    return new Error('A estrutura de vínculos financeiros ainda não foi aplicada no banco. Execute o 24a-a7-vinculos-financeiros.sql.');
  }
  if (error.code === 'PGRST202' || error.code === '42883') {
    return new Error('A geração de obrigações mensais ainda não foi aplicada. Execute o 33a-a14-vinculos-obrigacoes-caixa.sql.');
  }
  if (error.code === '23505') {
    return new Error('Já existe um vínculo ativo deste tipo para este profissional neste tenant.');
  }
  if (error.code === '42501') {
    return new Error('Somente administradores podem consultar ou alterar vínculos financeiros.');
  }
  return new Error(error.message || 'Não foi possível salvar o vínculo financeiro.');
}
